import { useMemo, useState } from 'react';
import { Option } from '@/components/common/ReactSelect5';
import I18n from '@/modules/i18n';
import stripStandardsAliasPrefix from '@/utils/stripStandardsAliasPrefix';
import {
  CorrelationCategory, Filters, GRADE_ORDER, ModelCorrelation, NO_FILTER,
} from '../types';

const EMPTY_FILTERS: Filters = {
  category: NO_FILTER, grade: NO_FILTER, search: NO_FILTER, state: NO_FILTER,
};

interface UseCorrelationFiltersParams {
  categories: CorrelationCategory[];
  correlations: ModelCorrelation[];
}

/**
 * Splits filtering into the two stages the footer reports on. Y is the count for the
 * current Category scope alone — State, Grade and search all narrow the visible rows (X).
 */
export default function useCorrelationFilters({ categories, correlations }: UseCorrelationFiltersParams) {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);

  const selectedCategory = categories.find(category => category.value === filters.category) ?? null;

  const categoryOptions: Option[] = useMemo(() => [
    { label: I18n.t('all_categories'), value: NO_FILTER },
    ...categories.map(({ label, value }) => ({ label, value })),
  ], [categories]);

  const inCategory = useMemo(() => (
    filters.category === NO_FILTER
      ? correlations
      : correlations.filter(correlation => correlation.category === filters.category)
  ), [correlations, filters.category]);

  // Scoping is a property of the category, so these render even when the item has no rows in it.
  // "All Categories" suppresses both.
  const showState = Boolean(selectedCategory?.has_state_scoping);
  const showGrade = Boolean(selectedCategory?.has_grade_scoping);

  const stateOptions = useMemo(() => {
    const byId = new Map<number, string>();
    inCategory.forEach(({ state_id: id, state_name: name }) => {
      if (id !== null && name) byId.set(id, name);
    });

    return [...byId.entries()]
      .map(([value, label]) => ({ label, value: String(value) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [inCategory]);

  const gradeOptions = useMemo(() => {
    const present = new Set(inCategory.map(correlation => correlation.grade).filter(Boolean));

    return GRADE_ORDER.filter(grade => present.has(grade)).map(grade => ({ label: grade, value: grade }));
  }, [inCategory]);

  // Built once per fetch rather than per keystroke: at 7.5k rows, rebuilding these strings on every
  // character was ~30k transient allocations a keypress.
  const haystacks = useMemo(() => new Map(correlations.map(correlation => [
    correlation.id,
    `${correlation.standard ?? ''} ${stripStandardsAliasPrefix(correlation.description, correlation.standard)}`
      .toLowerCase(),
  ])), [correlations]);

  // Kept separate from the search pass so typing doesn't re-run the dropdown predicates.
  const scoped = useMemo(() => inCategory.filter((correlation) => {
    if (showState && filters.state !== NO_FILTER && String(correlation.state_id) !== filters.state) return false;
    if (showGrade && filters.grade !== NO_FILTER && correlation.grade !== filters.grade) return false;

    return true;
  }), [filters.grade, filters.state, inCategory, showGrade, showState]);

  const filtered = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    if (!term) return scoped;

    return scoped.filter(correlation => haystacks.get(correlation.id)?.includes(term));
  }, [filters.search, haystacks, scoped]);

  const isFiltered = Object.values(filters).some(value => value.trim() !== NO_FILTER);

  const setFilter = (name: keyof Filters, value: string) => {
    setFilters((current) => {
      // A new category brings its own states and grades; keeping the old ones would filter to nothing.
      if (name === 'category') return { ...current, category: value, grade: NO_FILTER, state: NO_FILTER };

      return { ...current, [name]: value };
    });
  };

  const hiddenCount = useMemo(
    () => inCategory.filter(correlation => correlation.hidden).length,
    [inCategory],
  );

  return {
    categoryCount: inCategory.length - hiddenCount,
    categoryOptions,
    clearFilters: () => setFilters(EMPTY_FILTERS),
    filtered,
    filters,
    gradeOptions,
    hiddenCount,
    isFiltered,
    setFilter,
    showGrade,
    showState,
    stateOptions,
  };
}
